import { Services } from "@ca.vmedia.tv/services";
import { VodContentType, type Genre, ShowTypes, type OnDemandData } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { useApplicationStore } from "@/stores/Application";
import { OnDemandCardData, type ShowAllCardData} from "@/components/Cards/types";
import { useExtraCards } from "@/composables/useExtraCards";
import { useLinesVodStore } from "@/stores/LinesVod";
import { useMostPopularVod } from "@/stores/MostPopularVod";
import { GenresSubitemsRequest } from "@/stores/Genres/types/GenresSubitemsRequest";
import { Logger } from '@/utils';
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { ExposeInterface } from './types';
import type { AxiosError } from "axios";

export const useLinesVodDetailGenreStore = defineStore('LinesVodDetailGenre', (): ExposeInterface => {
    const { 
        emptyOnDemandData
    } = useEmptyLine();

    const MostPopularVodPageStore = useMostPopularVod();
    const {
        fetchMostPopularVodDetailGenre,
    } = MostPopularVodPageStore;

    const { hasShowAll, getCardShowAll } = useExtraCards();
    const LinesVodStore = useLinesVodStore();
    const {
        getInternalGenres,
    } = storeToRefs(LinesVodStore);

    const ApplicationStore = useApplicationStore();
    const { getFilterVodParams } = storeToRefs(ApplicationStore);

    const fetchMostPopularDetailGenre = (genre: Genre): void => {
        fetchMostPopularVodDetailGenre(
            new GenresSubitemsRequest(
                genre,
                AppConstants.MostPopularItemsInLine, 
                1
            )
        );
    };

    const _detailGenre: Ref<Genre | null> = ref(null);
    const setCurrentGenre = async (id: number): Promise<Genre | null> => {
        return new Promise((resolve) => {
            const currentGenre = getInternalGenres.value?.find((element: Genre) => element.id === id) ?? null;

            _detailGenre.value = currentGenre;
            resolve(currentGenre);
        });
    };

    const _newShowsGenres: Ref<OnDemandData | null> = ref(null);
    const fetchNewShowsGenres = async (genre: Genre): Promise<void> => {
        try {
            if (_newShowsGenres.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            if (showType === ShowTypes.Movie) {
                return;
            }

            _newShowsGenres.value = await Services.Instance.vod.getVodContent(
                VodContentType.NewSeries,
                AppConstants.ItemsInLine,
                1,
                genre,
                showType
            );
        } catch (error) {
            _newShowsGenres.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsNewShowsGenres = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = _newShowsGenres.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const _newMoviesGenres: Ref<OnDemandData | null> = ref(null);
    const fetchNewMoviesGenres = async (genre: Genre): Promise<void> => {
        try {
            if (_newMoviesGenres.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;
            if (showType === ShowTypes.Series) {
                return;
            }

            _newMoviesGenres.value = await Services.Instance.vod.getVodContent(
                VodContentType.NewMovies,
                AppConstants.ItemsInLine,
                1,
                genre,
                showType
            );
        } catch (error) {
            _newMoviesGenres.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsNewMoviesGenres = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = _newMoviesGenres.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    }); 
    const getCardsGenre = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        if (_detailGenre.value === null || _detailGenre.value.content === null || _detailGenre.value.content.items === null) {
            return null;
        }

        const items = _detailGenre.value.content.items.map((item) => new OnDemandCardData(item));

        if (hasShowAll(_detailGenre.value.content.paging)) {
            return [...items, ...getCardShowAll(items, _detailGenre.value.content.paging)];
        }
        return items;
    });

    const _counterRows: Ref<number> = ref(AppConstants.VodCounterLines);
    const getCounterLinesVodDetailGenre = computed((): number => _counterRows.value);
    const increaseCounterLinesVodDetailGenre = (): void => {
        _counterRows.value += 1;
    };
    const clearAllLinesVodDetailGenreStore = (): void => {
        _counterRows.value = AppConstants.VodCounterLines;
        _newMoviesGenres.value = null;
        _newShowsGenres.value = null;
        _detailGenre.value = null;
    };
    return {
        fetchNewShowsGenres,
        fetchNewMoviesGenres,
        fetchMostPopularDetailGenre,
        getCardsNewShowsGenres,
        getCardsNewMoviesGenres,
        getCounterLinesVodDetailGenre,
        getCardsGenre,
        setCurrentGenre,
        increaseCounterLinesVodDetailGenre,
        clearAllLinesVodDetailGenreStore
    };
});